import { format, parse } from 'date-fns'
import { CalendarDays } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Calendar } from './calendar'
import { fieldClass } from './field-class'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

const VALUE_FORMAT = "yyyy-MM-dd'T'HH:mm"

interface DateTimeInputProps {
  /** Same format as a native datetime-local input: "2026-10-16T14:30", or '' when empty. */
  value: string
  onChange: (value: string) => void
  id?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  'aria-invalid'?: boolean
}

/** Calendar + time picker in a popover; a drop-in for <input type="datetime-local">. */
export function DateTimeInput({ value, onChange, id, placeholder = 'Pick date and time', disabled, className, ...aria }: DateTimeInputProps) {
  const date = value ? parse(value, VALUE_FORMAT, new Date()) : undefined
  const valid = date && !Number.isNaN(date.getTime()) ? date : undefined
  const time = valid ? format(valid, 'HH:mm') : '09:00'

  const emit = (day: Date | undefined, hhmm: string) => {
    if (!day) return onChange('')
    const [h, m] = hhmm.split(':').map(Number)
    const next = new Date(day)
    next.setHours(h || 0, m || 0, 0, 0)
    onChange(format(next, VALUE_FORMAT))
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-invalid={aria['aria-invalid']}
          className={cn(fieldClass, 'flex h-8 items-center gap-2 text-left', !valid && 'text-faint', className)}
        >
          <CalendarDays className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="truncate">{valid ? format(valid, 'd MMM yyyy, h:mm a') : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={valid} onSelect={(d) => emit(d, time)} initialFocus />
        <div className="flex items-center justify-between gap-3 border-t border-border-subtle px-4 py-3">
          <label className="text-sm text-ink" htmlFor={id ? `${id}-time` : undefined}>
            Time
          </label>
          <input
            id={id ? `${id}-time` : undefined}
            type="time"
            value={time}
            onChange={(e) => emit(valid ?? new Date(), e.target.value)}
            className={cn(fieldClass, 'h-8 w-32 py-1')}
          />
        </div>
        {valid && (
          <div className="px-4 pb-3">
            <button type="button" className="text-sm text-link hover:underline" onClick={() => onChange('')}>
              Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
